import type { ExistingOrgStatus } from './services/wizard';
import { normalizeStoredLanguage, type LanguageCode } from './i18n/types';

/** Install-flow pages that must never open for a finished (or ambiguous) org. */
export const INSTALL_UI_PAGES = [
  'welcome',
  'setup',
  'install-summary',
  'provisioning',
] as const;

export type InstallUiPage = (typeof INSTALL_UI_PAGES)[number];

/** Step indicator / install chrome. Complete is the last install step; Landing is not. */
export const INSTALL_CHROME_PAGES = [
  'welcome',
  'setup',
  'install-summary',
  'complete',
] as const;

/**
 * Action flags that must only come from the live URL query string.
 * Never copy these out of the OAuth `state` blob into appParams.
 */
export const OAUTH_STATE_BLOCKLIST = new Set(['fresh', 'preview']);

/**
 * Vite `--mode` for the deploy:dev / local-dev bundles. `vite build --mode prod`
 * sets MODE to `prod` (dead). Do not use `import.meta.env.DEV` — that is false
 * for a production-NODE_ENV deploy:dev build.
 */
export const DEV_PREVIEW_ENABLED =
  import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'development';

export type WizardPreviewPage =
  | 'welcome'
  | 'setup'
  | 'install-summary'
  | 'provisioning'
  | 'complete'
  | 'landing'
  | 'settings'
  | 'actions'
  | 'signin'
  | 'loading'
  | 'error'
  | 'uninstalling';

export const WIZARD_PREVIEW_PAGES: readonly WizardPreviewPage[] = [
  'welcome',
  'setup',
  'install-summary',
  'provisioning',
  'complete',
  'landing',
  'settings',
  'actions',
  'signin',
  'loading',
  'error',
  'uninstalling',
];

const PREVIEW_LOADING_KEYS = ['loading', 'loading-authenticating', 'loading-checking', 'loading-validating'] as const;

const PREVIEW_LOGIN_LIMIT_STATES = ['connected', 'not-connected', 'invalid-client', 'checking', 'mismatch', 'delayed'] as const;
const PREVIEW_LOGIN_LIMIT_REPORTS = ['concurrent', 'named', 'none', 'mixed', 'failed', 'slow', 'late'] as const;

export type WizardPreview = {
  page: WizardPreviewPage;
  scanEnabled: boolean;
  /** `?loginLimit=1` shows the Login Limit section and the Settings row. */
  loginLimit: boolean;
  /** `?llState=` sets the Login Limit connection the preview starts from. */
  loginLimitState: (typeof PREVIEW_LOGIN_LIMIT_STATES)[number];
  /**
   * `?llReport=` sets what the billing report shows after Connect or Check again (`mixed`: both
   * models, no model), never finishes (`slow`), or names concurrent only after the 4-minute wait (`late`).
   */
  loginLimitReport: (typeof PREVIEW_LOGIN_LIMIT_REPORTS)[number];
  aiActionsEnabled: boolean;
  /** `?conversations=0` previews a Login Limit only org. Missing = on. */
  conversations: boolean;
  errorKind: 'permissions' | 'unavailable' | 'generic';
  loadingKey: (typeof PREVIEW_LOADING_KEYS)[number];
  dialog: boolean;
  /** Forces the screen language in preview. Not saved. */
  lang: LanguageCode | null;
};

function isTruthyQueryFlag(value: string | null): boolean {
  return value === '1' || value === 'true';
}

function isPreviewPage(value: string | null): value is WizardPreviewPage {
  return !!value && (WIZARD_PREVIEW_PAGES as readonly string[]).includes(value);
}

/**
 * Dev-only preview of every wizard screen, with sample data and no Genesys
 * login. Prod builds (`--mode prod`) take the constant `null` path.
 * `?lang=he` forces that language for the screenshot and is not saved.
 */
export function parseWizardPreview(search: string, enabled: boolean): WizardPreview | null {
  if (!enabled) return null;
  const params = new URLSearchParams(search);
  const page = params.get('preview');
  if (!isPreviewPage(page)) return null;
  const loading = params.get('loading');
  const errorKind = params.get('previewError');
  const loginLimitState = params.get('llState');
  const loginLimitReport = params.get('llReport');
  return {
    page,
    scanEnabled: isTruthyQueryFlag(params.get('scanEnabled')),
    loginLimit: isTruthyQueryFlag(params.get('loginLimit')),
    loginLimitState: (PREVIEW_LOGIN_LIMIT_STATES as readonly string[]).includes(loginLimitState ?? '')
      ? loginLimitState as WizardPreview['loginLimitState']
      : 'connected',
    loginLimitReport: (PREVIEW_LOGIN_LIMIT_REPORTS as readonly string[]).includes(loginLimitReport ?? '')
      ? loginLimitReport as WizardPreview['loginLimitReport']
      : 'concurrent',
    aiActionsEnabled: isTruthyQueryFlag(params.get('aiActionsEnabled')),
    conversations: !['0', 'false'].includes(params.get('conversations') ?? ''),
    errorKind: errorKind === 'permissions' || errorKind === 'unavailable' ? errorKind : 'generic',
    loadingKey: (PREVIEW_LOADING_KEYS as readonly string[]).includes(loading ?? '')
      ? loading as WizardPreview['loadingKey']
      : 'loading-authenticating',
    dialog: isTruthyQueryFlag(params.get('dialog')),
    lang: normalizeStoredLanguage(params.get('lang')),
  };
}

export function readDevWizardPreview(): WizardPreview | null {
  return parseWizardPreview(window.location.search, DEV_PREVIEW_ENABLED);
}

/**
 * What Landing offers beyond Insights. Settings is for every CloudBriz Admin,
 * except in a Login Limit only org (`conversationsEnabled: false`) that no
 * longer has Login Limit either, where Settings would be empty. Conversation
 * Actions also needs its org flag and conversations.
 */
export function landingDestinations(flags: {
  canManageSettings: boolean;
  loginLimitEnabled: boolean;
  aiActionsEnabled: boolean;
  conversationsEnabled: boolean;
}): { settings: boolean; actions: boolean } {
  const { canManageSettings, loginLimitEnabled, aiActionsEnabled, conversationsEnabled } = flags;
  return {
    settings: canManageSettings && (conversationsEnabled || loginLimitEnabled),
    actions: canManageSettings && aiActionsEnabled && conversationsEnabled,
  };
}

export function isInstallUiPage(page: string): page is InstallUiPage {
  return (INSTALL_UI_PAGES as readonly string[]).includes(page);
}

export function isInstallChromePage(page: string): boolean {
  return (INSTALL_CHROME_PAGES as readonly string[]).includes(page);
}

export function isFullyProvisioned(status: ExistingOrgStatus): boolean {
  return status.exists === true && status.hasCredentials === true;
}

/** `?fresh=true` after uninstall only. Ignores hash and OAuth state. */
export function isFreshFromUrlQuery(): boolean {
  return new URLSearchParams(window.location.search).get('fresh') === 'true';
}

/**
 * Restore Genesys launch params from the PKCE `state` blob.
 * URL-explicit values win. `fresh` (and any other blocklisted key) is never copied.
 */
export function restoreLaunchParamsFromOAuthState(
  appParams: Record<string, string>,
  stateRaw: string,
): void {
  const restored = JSON.parse(stateRaw) as Record<string, unknown>;
  for (const [key, value] of Object.entries(restored)) {
    if (OAUTH_STATE_BLOCKLIST.has(key)) continue;
    if (typeof value === 'string' && !appParams[key]) {
      appParams[key] = value;
    }
  }
}

export type PostAuthRoute =
  | { kind: 'landing'; reason: 'fully-provisioned' | 'genesys-oauth-clients' }
  | { kind: 'install' }
  | { kind: 'error'; reason: 'check-failed' | 'ambiguous' };

/**
 * Decide where to go after auth + org checks.
 *
 * Fully provisioned → Landing.
 * check-org miss + Mediabriz wizard OAuth clients still in Genesys → Landing
 * (never a second install).
 * check-org miss + OAuth-client lookup failed → error (fail closed).
 * Otherwise the org is proven not fully provisioned → install UI.
 */
export function resolvePostAuthRoute(
  status: ExistingOrgStatus,
  oauthClientCount: number | null,
): PostAuthRoute {
  if (isFullyProvisioned(status)) {
    return { kind: 'landing', reason: 'fully-provisioned' };
  }

  if (!status.exists) {
    if (oauthClientCount === null) {
      return { kind: 'error', reason: 'ambiguous' };
    }
    if (oauthClientCount > 0) {
      return { kind: 'landing', reason: 'genesys-oauth-clients' };
    }
  }

  return { kind: 'install' };
}
